import React, { useEffect, useRef } from "react";
import { Redirect, useLocation } from "react-router-dom";
import { Typography, Box, Breadcrumbs } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useSelector, useDispatch } from "react-redux";
import { themes, theme } from "services/constants";
import { FilterProvider } from "pages/FilterContext";
import { setFilterApi } from "services/Redux/userManagament";
import { EarningShift } from "./ShiftEarnings";
import { EarningOvertime } from "./Overtime";
import { Penalties } from "./Penalties";
import { PayInputLeave } from "./Leave";
import { Incentives } from "./Incentives";
import { Incentive as IncentivePayout } from "./Incentives/IncentivePayout";
import { LoansAndAdvances } from "./LoansAndAdvances";
import { PayrunCycleProvider } from "pages/PayRoll/PayProcessing/PayrollCycleRun/PayrollCycleRunContext";
import { PayInputBonus } from "./Bonus";
import { PayInputReimbursements } from "./Reimbursements";
import { PayInputDamageAndRecovery } from "./DamageAndRecovery";
import { PayInputSalaryHolds } from "./SalaryHolds";
import { PayInputSecurityDeposit } from "./SecurityDeposit";
import { PayInputPayrollAdjustments } from "./PayrollAdjustments";

// Payroll > Pay Inputs is a sidebar flyout (see routes.js) and the only way
// to move between these screens; the old Earnings / Deductions tab lists
// were removed. Each flyout item has its own URL under
// /admin/payroll/pay-inputs/..., matching this folder's sub-folders.
export const PAY_INPUTS_BASE_PATH = "/admin/payroll/pay-inputs";

// URL segment -> screen. `module`/`subModule` are the backend names the old
// screens used to find the subModuleId, kept as-is. `ownLayout` screens
// bring their own page layout and breadcrumb. `designLayout` screens are
// redesigned lists that draw their own page (breadcrumb, header, sticky
// toolbar) but still get the FilterProvider here. Screens with neither are
// "not implemented yet" placeholders.
const PAY_INPUT_SCREENS = {
  "shift-earnings": {
    label: "Shift Earnings",
    module: "Earnings",
    subModule: "Shift",
    Component: EarningShift,
    designLayout: true,
  },
  overtime: {
    label: "Overtime",
    module: "Earnings",
    subModule: "Overtime",
    Component: EarningOvertime,
    designLayout: true,
  },
  // Leave has no sub-module of its own yet; it uses the Earnings > Shift
  // access (sub-module id for saved views, and the edit check in the screen).
  leave: {
    label: "Leave",
    module: "Earnings",
    subModule: "Shift",
    Component: PayInputLeave,
    designLayout: true,
  },
  // Incentive rules (was the separate Payroll > Incentive page).
  incentives: { label: "Incentives", Component: Incentives, ownLayout: true },
  // Pay-cycle incentive processing (was Pay Processing > Incentive Payout).
  "incentive-payout": {
    label: "Incentive Payout",
    module: "Payroll Processing",
    subModule: "Incentive",
    Component: IncentivePayout,
    withPayrunProvider: true,
  },
  // Bonus types/settings on the real API; runs and entries on sample data
  // until the backend's pay-inputs/bonus/ API exists. No provisioning
  // submodule yet, so no saved views.
  bonus: { label: "Bonus", Component: PayInputBonus, designLayout: true },
  // Types/settings on the real API; claims on sample data until the
  // backend's Pay Inputs reimbursement API exists. No provisioning
  // submodule yet, so no saved views.
  reimbursements: { label: "Reimbursements", Component: PayInputReimbursements, designLayout: true },
  penalties: {
    label: "Penalties",
    module: "Deductions",
    subModule: "Penalties",
    Component: Penalties,
    designLayout: true,
  },
  "damage-and-recovery": {
    label: "Damage & Recovery",
    Component: PayInputDamageAndRecovery,
  },
  // Loans and salary advances (advance/request/ APIs). Uses the old
  // Pay Processing > Advance access.
  "loans-and-advances": {
    label: "Loans & Advances",
    module: "Payroll Processing",
    subModule: "Advance",
    Component: LoansAndAdvances,
    designLayout: true,
  },
  "salary-holds": { label: "Salary Holds", Component: PayInputSalaryHolds },
  "security-deposit": {
    label: "Security Deposit",
    Component: PayInputSecurityDeposit,
  },
  "payroll-adjustments": {
    label: "Payroll Adjustments",
    Component: PayInputPayrollAdjustments,
  },
};

const getPayInputSegment = (pathname) => {
  let path = pathname;
  while (path.endsWith("/")) path = path.slice(0, -1);
  if (!path.startsWith(`${PAY_INPUTS_BASE_PATH}/`)) return "";
  return path
    .slice(PAY_INPUTS_BASE_PATH.length + 1)
    .split("/")[0]
    .toLowerCase();
};

export const PayInputs = () => {
  const { pathname } = useLocation();
  const segment = getPayInputSegment(pathname);
  const screen = PAY_INPUT_SCREENS[segment];
  const { domain, userScreenAccess, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );
  const dispatch = useDispatch();

  const subModuleId = screen?.module
    ? userScreenAccess?.module_details
        ?.find((mod) => mod.module_name === screen.module)
        ?.submodules?.find((sub) => sub.sub_module_name === screen.subModule)
        ?.sub_module_id
    : undefined;

  // Same as the old tab click: switching screens asks the list to refetch.
  const previousSegmentRef = useRef(segment);
  useEffect(() => {
    if (previousSegmentRef.current === segment) return;
    previousSegmentRef.current = segment;
    dispatch(setFilterApi(true));
  }, [segment, dispatch]);

  // Old Components / Incentive URLs and unknown paths land on the matching
  // new screen.
  if (!screen) {
    let target = "shift-earnings";
    if (pathname.startsWith("/admin/payroll/components/deductions")) {
      target = "penalties";
    } else if (pathname.startsWith("/admin/payroll/incentive")) {
      target = "incentives";
    }
    return <Redirect to={`${PAY_INPUTS_BASE_PATH}/${target}`} />;
  }

  const { Component } = screen;

  if (screen.ownLayout) {
    return <Component />;
  }

  const content = <Component subModuleId={subModuleId} />;

  if (screen.designLayout) {
    return (
      <FilterProvider
        key={segment}
        domain={domain}
        userDetails={userDetails}
        subModuleId={subModuleId}
      >
        {content}
      </FilterProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: "flex",
          height: "94.5vh",
          backgroundColor: themes?.whiteColor,
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            minWidth: 0,
            backgroundColor: "#f5f5fd",
            transition: "all 0.3s ease",
          }}
        >
          <Breadcrumbs aria-label="breadcrumb" separator="›" sx={{ mb: 2 }}>
            <Typography>Payroll</Typography>
            <Typography>Pay Inputs</Typography>
            <Typography color={themes?.blueColor}>{screen.label}</Typography>
          </Breadcrumbs>

          {screen.module ? (
            <FilterProvider
              key={segment}
              domain={domain}
              userDetails={userDetails}
              subModuleId={subModuleId}
            >
              {screen.withPayrunProvider ? (
                <PayrunCycleProvider>{content}</PayrunCycleProvider>
              ) : (
                content
              )}
            </FilterProvider>
          ) : (
            <Component />
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};
